import { forkJoin, Observable, of } from 'rxjs';
import { defaultIfEmpty, map, take } from 'rxjs/operators';
import { DictionariesCollectionService } from './dictionaries/dictionaries-collection.service';
import { categoriesAdapter, CategoryElement } from './partial/categories.model';
import { Coding } from 'fhir/r4';

interface DataResource {
  id?: string;
}

export interface DataModel<Resource extends DataResource = DataResource> {
  id?: string;
  _categories?: { [categoryName: string]: CategoryElement[] };
  _resource?: Resource;
  patientOnly?: boolean;
}

export type ID = DataModel['id'];

export type ResourceOf<Model extends DataModel> = Model['_resource'];

interface DataAdapter<Model extends DataModel> {
  /**
   * converts Resource Instance to Data Model Instance
   *
   * @param resource Resource Instance object
   * @returns Data Model Instance
   */
  fromResource$(resource: ResourceOf<Model>): Observable<Model>;

  /**
   * converts Data Model Instance to Resource Instance
   *
   * @param entity  Data Model Instance object
   * @param options Options for Resource creation (patientId, ...)
   * @returns  Resource Instance
   */
  toResource$(entity: Model, options?: { [key: string]: any }): Observable<any>;

  /**
   * get category's list by categoryName
   *
   * @param name
   */
  getCategoryOptionsList$(name: string): Observable<CategoryElement[]>;
}

interface DataCategorySetting {
  categoryName: string; // start name with '_' if the category only for calculations and not for UI
  _optionsList?: CategoryElement[];
  required?: boolean;
}

abstract class DataAdapter<Model extends DataModel>
  implements DataAdapter<Model>
{
  protected clone<T extends {}>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  abstract categorySettings: DataCategorySetting[];

  protected abstract blankResource(): ResourceOf<Model>;

  protected abstract getCategoriesFromResource$(
    resource: ResourceOf<Model>,
    categoryName: string,
  ): Observable<CategoryElement[]>;
  protected abstract addCategoriesToResource(
    resource: ResourceOf<Model>,
    categoryName: string,
    categoryElements: CategoryElement[],
  ): void;
  protected abstract removeCategoriesFromResource(
    resource: ResourceOf<Model>,
    categoryName: string,
    categoryElements: CategoryElement[],
  ): void;

  fromResource$(
    resource: ResourceOf<Model>,
    responseBundle?: fhir4.Bundle,
  ): Observable<Model> {
    const _categories$: Observable<Model['_categories']> = forkJoin(
      this.categorySettings.reduce(
        (acc, { categoryName }) => ({
          ...acc,
          [categoryName]: this.getCategoriesFromResource$(
            resource,
            categoryName,
          ).pipe(take(1)),
        }),
        {},
      ),
    ).pipe(defaultIfEmpty({}));

    return _categories$.pipe(
      map((_categories) => {
        const entity: Model = {
          id: resource.id,
          _categories,
          _resource: this.clone(resource),
        } as Model;

        return entity;
      }),
    );
  }

  toResource$(
    entity: Model,
    options?: { [key: string]: any },
  ): Observable<ResourceOf<Model>> | Observable<fhir4.Bundle> {
    const hasInitResource = entity._resource && !options?.useBlankResource;
    const resource = hasInitResource
      ? this.clone(entity._resource)
      : this.blankResource();

    this.categorySettings.forEach(({ categoryName }) => {
      const categoryElementsToAdd = entity?._categories?.[categoryName];

      if (hasInitResource) {
        this.getCategoriesFromResource$(resource, categoryName).subscribe(
          (list) => {
            const categoryElementsToRemove = list.filter(
              (categoryElement) =>
                !categoryElementsToAdd.find(
                  ({ id }) => categoryElement.id === id,
                ),
            );

            this.removeCategoriesFromResource(
              resource,
              categoryName,
              categoryElementsToRemove,
            );
          },
        );
      }

      this.addCategoriesToResource(
        resource,
        categoryName,
        categoryElementsToAdd,
      );
    });

    return of(resource);
  }

  toResource(...args) {
    let res;
    this.toResource$.apply(this, args).subscribe((r) => (res = r));
    return res;
  }
}

export type DataFhirModel<R extends fhir4.Resource = fhir4.FhirResource> =
  DataModel<R> & { _resourceType: R['resourceType'] };

export interface DataFhirCategorySetting<Model extends DataFhirModel>
  extends DataCategorySetting {
  dictionaryIds: string[];
  resourcePosition?:
    | string
    | ((
        resource: ResourceOf<Model>,
      ) => fhir4.CodeableConcept | fhir4.CodeableConcept[]);
}
export abstract class DataFhirAdapter<
  Model extends DataFhirModel,
> extends DataAdapter<Model> {
  resourceType: ResourceOf<Model>['resourceType'];
  categorySettings: DataFhirCategorySetting<Model>[] = [];

  public readonly patientOnlySecurityLabelCoding: fhir4.Coding = {
    system: 'urn:oid:2.16.840.1.113883.3.37.1.9.42.2',
    code: 'norep',
    display: 'Not visible for representatives',
  };

  constructor(
    resourceType: ResourceOf<Model>['resourceType'],
    protected dictionariesCollection: DictionariesCollectionService,
  ) {
    super();
    this.resourceType = resourceType;
  }

  private getCategorySetting(name: string): DataFhirCategorySetting<Model> {
    return this.categorySettings.find(
      ({ categoryName }) => categoryName == name,
    );
  }

  /**
   * get part of Fhir.Resource where required categories are defined
   * @param resource
   * @param categoryName
   * @returns fhir4.CodeableConcept | fhir4.CodeableConcept[]
   */
  private getCategoryResource(
    resource: ResourceOf<Model>,
    categoryName: string,
  ): fhir4.CodeableConcept | fhir4.CodeableConcept[] {
    const { resourcePosition } = this.getCategorySetting(categoryName);

    if (typeof resourcePosition === 'string') {
      if (!resource[resourcePosition]) {
        // if not exist, should take template from blankResource
        resource[resourcePosition] = this.clone(this.blankResource())?.[
          resourcePosition
        ];
      }
      return resource[resourcePosition];
    }
    if (typeof resourcePosition === 'function')
      return resourcePosition(resource);
  }

  public getCategoryOptionsList$(name: string): Observable<CategoryElement[]> {
    const categorySetting = this.getCategorySetting(name);
    if (categorySetting?._optionsList) {
      return of(this.getCategorySetting(name)?._optionsList);
    }

    return this.dictionariesCollection.getListByIds$(
      categorySetting?.dictionaryIds,
    );
  }

  protected getCategoriesFromResource$(
    resource: ResourceOf<Model>,
    categoryName: string,
  ): Observable<CategoryElement[]> {
    return this.getCategoryOptionsList$(categoryName).pipe(
      map((optionsList) => {
        const categoriesResource = this.getCategoryResource(
          resource,
          categoryName,
        );
        const categorieOptions =
          categoriesAdapter.getCategories(categoriesResource);

        return optionsList?.filter(({ id }) =>
          categorieOptions.some((option) => option.id === id),
        );
      }),
    );
  }

  protected addCategoriesToResource(
    resource: ResourceOf<Model>,
    categoryName: string,
    categoryElements: CategoryElement[] = [],
  ): void {
    if (!categoryElements.length) return;

    const categoriesResource = this.getCategoryResource(resource, categoryName);
    if (!categoriesResource) return;

    categoryElements.forEach((categoryElement) =>
      categoriesAdapter.addCategory(categoriesResource, categoryElement),
    );
  }

  protected removeCategoriesFromResource(
    resource: ResourceOf<Model>,
    categoryName: string,
    categoryElements: CategoryElement[] = [],
  ): void {
    if (!categoryElements.length) return;

    const categoriesResource = this.getCategoryResource(resource, categoryName);
    if (!categoriesResource) return;

    categoryElements.forEach((categoryElement) =>
      categoriesAdapter.removeCategory(categoriesResource, categoryElement),
    );
  }

  /**
   * assign Patient to FhirResource as subject reference
   *
   * @param resource
   * @param patientId
   * @returns
   */
  protected assignPatient<R = ResourceOf<Model>>(
    resource: R,
    patientId?: string,
  ): R {
    return patientId
      ? { ...resource, subject: { reference: `Patient/${patientId}` } }
      : resource;
  }

  protected assignPatientWithIdentifier<R = ResourceOf<Model>>(
    resource: R,
    patientOid?,
  ): R {
    return patientOid
      ? {
          ...resource,
          subject: {
            type: 'Patient',
            identifier: {
              system: patientOid.system,
              value: patientOid.value,
            }, //PatientID
          },
          identifier: [
            {
              system: patientOid.system,
              value: patientOid.value,
            },
          ],
        }
      : resource;
  }

  /**
   * assign Practitioner to FhirResource as performer reference
   *
   * @param resource
   * @param practitionerId
   * @returns
   */
  protected assignPractitioner<R = ResourceOf<Model>>(
    resource: R,
    practitionerId?: string,
  ): R {
    return practitionerId
      ? {
          ...resource,
          performer: { reference: `Practitioner/${practitionerId}` },
        }
      : resource;
  }

  protected getPatientOnlySecurityLabel(resource: ResourceOf<Model>): Coding {
    if (resource && !this.isPatientOnlyCoding(resource?.meta?.security)) {
      return this.patientOnlySecurityLabelCoding;
    }
  }

  public isPatientOnlyCoding(
    coding: fhir4.Coding | fhir4.Coding[] | undefined,
  ): boolean {
    if (!coding) return false;

    return (Array.isArray(coding) ? coding : [coding]).some(
      ({ code, system }) =>
        code === this.patientOnlySecurityLabelCoding.code &&
        system === this.patientOnlySecurityLabelCoding.system,
    );
  }
}
